import React, { useState } from "react";
import styles from "./ItemList.module.scss";

import moment from "moment";
import { ItemsProps } from "../../types/item";
import { useAppDispatch } from "../../app/hooks";
import { setEditItem, toggleItemModal } from "../../features/items/itemsSlice";
import useConfirmPopup from "../../context/useConfirmPopup";

type Props = {
  items: ItemsProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeleteItem: (id: number) => void;
};

const ItemList = ({
  items,
  pageNumber,
  pageSize,
  isLoading,
  handleDeleteItem,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleEditItem = async (item: ItemsProps) => {
    await dispatch(setEditItem(item));
    dispatch(toggleItemModal(true));
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!"
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteItem(id);
      setIsActionLoading(0);
    }
  };

  return (
    <div className={styles.ItemList}>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Sr. No.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    ફોટો
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    આઈટમ નામ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    વજન
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    સેટ મા આવસે?
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    તારીખ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    એકશન્સ બટન
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-primary-2 font-bold text-xl text-center py-2"
                    >
                      રાહ જુઓ...
                    </td>
                  </tr>
                ) : (
                  <>
                    {items.length > 0 ? (
                      <>
                        {items.map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(pageNumber - 1) * pageSize + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {item.image ? (
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="w-12 h-12 object-cover rounded border border-primary-1"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-200 rounded border border-gray-300 flex items-center justify-center">
                                  <span className="text-gray-400 text-xs">No Img</span>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.name || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.weight || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                              {item.isinset ? (
                                <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
                                  હા
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">
                                  ના
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {moment(item.created_at).format(
                                "DD-MM-YYYY hh:mm A"
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {isActionLoading === item.id ? (
                                <p className="text-primary-2 font-bold text-right text-lg">
                                  રાહ જુઓ...
                                </p>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEditItem(item)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRow(item.id)}
                                    className="text-red-600 hover:text-red-900 ml-4"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className="text-primary-2 font-bold text-xl text-center py-2"
                        >
                          આઈટમ ની વિગતો મળી નથી
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ItemList;
